import type { ISplitOptions } from "../../models/text/ISplitOptions.ts";

/**
 * Simple boolean checks over a parsed `string-split` attribute
 * (`ISplitOptions`), shared across StringSplit's connect/resize/fit code
 * paths so they don't each redefine the same "does this option combination
 * mean X" check.
 */
export class SplitOptionsPredicates {
  /** True when the split produces `.-s-line` spans (line, wordLine, or charLine). */
  static hasLineDrivenSplit(options: ISplitOptions): boolean {
    return (
      (options.line?.length ?? 0) > 0 ||
      (options.wordLine?.length ?? 0) > 0 ||
      (options.charLine?.length ?? 0) > 0
    );
  }

  /** True when char-level split is active (char, charLine, or charWord). */
  static hasCharDrivenSplit(options: ISplitOptions): boolean {
    return (
      (options.char?.length ?? 0) > 0 ||
      (options.charLine?.length ?? 0) > 0 ||
      (options.charWord?.length ?? 0) > 0
    );
  }

  /** True when a resize should re-split: line-driven layout or fit both depend on element width. */
  static needsWidthRebuild(options: ISplitOptions): boolean {
    return SplitOptionsPredicates.hasLineDrivenSplit(options) || options.fit === true;
  }

  /** True when a font-load event should force a re-split (same condition as a width rebuild today). */
  static needsForcedRebuildOnFontLoad(options: ISplitOptions): boolean {
    return SplitOptionsPredicates.needsWidthRebuild(options);
  }
}
